import { useQuery } from '@tanstack/react-query';
import { PricingData } from './types';
import { useCallback } from 'react';

export const PRICING_QUERY_KEY = ['pricing'] as const;

/**
 * Hook to get pricing data for a single market from React Query cache.
 * Uses `select` to pick a single market from the allPricing map,
 * so the component only re-renders when that market's data changes.
 */
export function usePricingData(marketKey: string | undefined) {
	const select = useCallback(
		(data: Map<string, PricingData>) =>
			marketKey ? data?.get(marketKey) : undefined,
		[marketKey]
	);

	return useQuery<Map<string, PricingData>, Error, PricingData | undefined>({
		queryKey: PRICING_QUERY_KEY,
		queryFn: () => new Map(),
		enabled: false,
		staleTime: Infinity,
		select,
	});
}

/**
 * Hook to get all pricing data from React Query cache.
 * Returns a Map of marketKey -> PricingData.
 */
export function useAllPricingData() {
	return useQuery<Map<string, PricingData>>({
		queryKey: PRICING_QUERY_KEY,
		queryFn: () => new Map(),
		enabled: false,
		staleTime: Infinity,
	});
}
